import { Col, Row } from 'antd';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { CloseBtn } from '@/public/closeBtn';
import { UserAvatar } from '@/components/UserAvatar/UserAvatar';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { SolgariApiService } from '@/services/solgari-api/solgari-api.service';
import { ResponseField } from '@/components/ResponseField/ResponseField';
import { observer } from 'mobx-react-lite';
import { Dynamics } from '@/services/dynamics/dynamics';
import { COMMON } from '@/configs/main.config';
import { useStore } from '@/stores';
import { saveChatNoteEntity } from '@/shared/helpers/save-chat-note.helper';
import { ChatTransfer } from '@/components/ChatTransfer/ChatTransfer';
import { formDate } from '@/services/utils/formatDate';
import { IChatMessage } from '@/shared/interfaces/chat-message.interface';
import { FaFileAlt } from 'react-icons/fa';
import { UrlRegex } from '@/shared/constants/regex';
import ImageWithLoader from '../ImageWithLoader/ImageWithLoader';
import { CALL_PAGE_TYPE } from '@/shared/constants/callPage';
import './ChatDetails.scss';

export const ChatDetails = observer(() => {
  const {
    chatsStore: {
      chatDetails,
      getChatDetailsFromApi,
      getChatsFromApi,
      updateChat,
    },
    callStore: { callPage },
  } = useStore();

  const { page } = callPage;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showTransferModal, setTransferModalStatus] = useState<boolean>(false);

  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();

  const { id: chatId } = params;
  const chatContainerView = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    const element: HTMLDivElement | null = chatContainerView.current;
    if (element) element.scrollTo(0, element.scrollHeight);
  };

  const getMessages = async (id: string) => {
    setIsLoading(true);
    await getChatDetailsFromApi(id);
    setIsLoading(false);
  };

  const onSendMessage = async (text: string) => {
    const clearValue = text.trim();
    await SolgariApiService.sendNewMessage(chatId, clearValue);
    await getChatDetailsFromApi(chatId);
  };

  const refreshChatWindow = async () => {
    await getChatDetailsFromApi(chatId);
  };

  const onEndChat = async () => {
    await SolgariApiService.endConversation(chatId);
    await updateChat(chatDetails);
    await getChatDetailsFromApi(chatId);
  };

  const onStopChat = async () => {
    await SolgariApiService.closeConversation(chatId);
    await getChatsFromApi();
    handleCloseChat();
  };

  const openChatDetails = async (chatId: string) => {
    const {
      user = COMMON.UNKNOWN_ENTITY,
      channelProvider,
      externalCustomerIdentity,
    } = chatDetails;
    await saveChatNoteEntity(
      chatId,
      externalCustomerIdentity,
      channelProvider,
      user,
    );
    Dynamics.openChatNote(chatId);
  };

  const onChatTransfer = () => {
    setTransferModalStatus(true);
  };

  const onChatTransferModalClose = () => {
    setTransferModalStatus(false);
  };

  const formatBytesToMB = (bytes: number): string => {
    const mb = bytes / (1024 * 1024);
    return mb.toFixed(2);
  };

  const renderMessage = (
    message: IChatMessage,
    index: number,
  ): ReactElement[] | null => {
    const isLast = index === messages.length - 1;
    const { textMessage, attachmentMessage, messageId, messageMode, chatId } =
      message;

    if (attachmentMessage?.type.toLowerCase() === 'image') {
      const imageElement: ReactElement = (
        <ImageWithLoader
          key={`${messageId}-img`}
          fetchImage={(): Promise<Blob> =>
            SolgariApiService.getImage(chatId, message)
          }
          alt={attachmentMessage.name as string}
          onClick={(url: string) => window.open(url, '_blank')}
          onLoad={isLast ? scrollToBottom : undefined}
        />
      );

      const textElement: ReactElement[] = textMessage?.text
        ? [<span key={`${messageId}-text`}>{textMessage.text}</span>]
        : [];

      return [imageElement, ...textElement];
    }

    if (attachmentMessage) {
      const extension: string = attachmentMessage.extension;
      const baseName: string =
        messageMode === 'Inbound'
          ? `Attachment.${extension}`
          : `${attachmentMessage.name}.${extension}`;
      const fileSize: string = `${formatBytesToMB(attachmentMessage.size)} MB`;

      return [
        <div
          key={baseName}
          className={`chat-msg ${messageMode?.toLowerCase()} file`}
        >
          <div
            className="file-link"
            onClick={(): Promise<void> =>
              SolgariApiService.downloadFile(chatId, message)
            }
            title="Click to download"
          >
            <FaFileAlt />
            <span>{baseName}</span>
          </div>
          <div className="file-name">{fileSize}</div>
        </div>,
      ];
    }

    if (!textMessage) return null;

    const parts: string[] = textMessage.text.split(UrlRegex);

    return parts.map<ReactElement>((part: string, i: number) =>
      UrlRegex.test(part) ? (
        <a
          key={`${messageId}-link-${i}`}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
        >
          {part}
        </a>
      ) : (
        <span key={`${messageId}-text-${i}`}>{part}</span>
      ),
    );
  };

  useEffect(() => {
    if (location.state?.skipOpening || !chatDetails) {
      return;
    }
    openChatDetails(chatId);
  }, [chatId, location.state, chatDetails]);

  useEffect(() => {
    getMessages(chatId);
  }, [params, params.id, chatId]);

  useEffect(() => {
    if (isLoading || !chatContainerView?.current) {
      return;
    }

    scrollToBottom();
  }, [chatDetails, isLoading]);

  const handleCloseChat = () => {
    const from = location.state?.from ?? '/dial-pad';

    navigate(from);
  };

  if (!chatDetails) {
    return <div className="chat-details__wrong">Loading...</div>;
  }

  const {
    user = COMMON.UNKNOWN_ENTITY,
    messages,
    channelProvider,
  } = chatDetails;
  const [firstName = '', lastName = ''] = user?.fullName
    ? user.fullName.split(' ')
    : ' ';

  return (
    <div className="chat-details">
      <Row className="active-history-container">
        <Col span={5} className="avatar-container">
          <UserAvatar
            firstName={firstName}
            lastName={lastName}
            status={''}
            avatar={channelProvider}
          />
        </Col>
        <Col span={15}>
          <div>
            {firstName} {lastName}
          </div>
        </Col>
        <Col span={4}>
          <button
            data-testid="close-chat-details"
            onClick={handleCloseChat}
            className="close"
          >
            <CloseBtn />
          </button>
        </Col>
      </Row>
      <div>
        <div>
          {messages?.length ? (
            <Row
              className={`chat-container ${page === CALL_PAGE_TYPE.MINIMIZED ? 'shortened' : ''}`}
              ref={chatContainerView}
            >
              <Col span={24}>
                {messages.map((msg: IChatMessage, index: number) => (
                  <div key={msg.messageId}>
                    <div
                      className={`chat-msg ${msg.messageMode === 'Outbound' ? '-admin ant-col-offset-8' : 'ant-col-16 inbound-msg'}`}
                    >
                      {renderMessage(msg, index)}
                    </div>
                    <div className="date">{formDate(msg.createdTimestamp)}</div>
                  </div>
                ))}
              </Col>
            </Row>
          ) : (
            <div
              className={`chat-container${page === CALL_PAGE_TYPE.MINIMIZED ? ' shortened' : ''}`}
            >
              Start a new conversation
            </div>
          )}
        </div>
        <Row className="active-history-container">
          <ResponseField
            chat={chatDetails}
            sendMessage={onSendMessage}
            refreshChat={refreshChatWindow}
            closeChat={onStopChat}
            endChat={onEndChat}
            onChatTransfer={onChatTransfer}
          />
        </Row>
      </div>
      <ChatTransfer
        showTransferModal={showTransferModal}
        onChatTransferModalClose={onChatTransferModalClose}
      />
    </div>
  );
});
